import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { RiCloudLine, RiLoader4Line } from '@remixicon/react';
import { useSchemaDesignerStore } from '@/stores/schema-designer-store';
import { toast } from 'sonner';

export const SaveCloudDialog = () => {
  const { saveToCloud, isSaving, nodes, currentSchemaId, savedSchemas } = useSchemaDesignerStore();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  // Check if we're updating an existing cloud schema
  const currentSchema = savedSchemas.find((s) => s.id === currentSchemaId);
  const isCloudSchema = currentSchemaId && !currentSchemaId.startsWith('schema-');
  const isUpdating = isCloudSchema && currentSchema;

  // Pre-fill form when opening dialog for existing schema
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && isUpdating && currentSchema) {
      setName(currentSchema.name);
      setDescription(currentSchema.description || '');
      setIsPublic(currentSchema.isPublic || false);
    } else if (!newOpen) {
      // Reset form when closing
      if (!isUpdating) {
        setName('');
        setDescription('');
        setIsPublic(false);
      }
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a schema name');
      return;
    }

    if (nodes.length === 0) {
      toast.error('Cannot save an empty schema');
      return;
    }

    try {
      await saveToCloud(name, description, isPublic);
      toast.success(isUpdating ? `Schema "${name}" updated` : `Schema "${name}" saved to cloud`);
      setOpen(false);
      if (!isUpdating) {
        setName('');
        setDescription('');
        setIsPublic(false);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save schema');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 bg-background shadow-sm"
          disabled={nodes.length === 0}
        >
          <RiCloudLine className="h-4 w-4" />
          {isUpdating ? 'Update Cloud' : 'Save to Cloud'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isUpdating ? 'Update Cloud Schema' : 'Save Schema to Cloud'}</DialogTitle>
          <DialogDescription>
            {isUpdating
              ? 'Update your cloud schema with the latest changes.'
              : 'Save your schema to the cloud to access it from anywhere and share with your team.'
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Schema Name *</Label>
            <Input
              id="name"
              placeholder="e.g., E-commerce Database"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description of your schema..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSaving}
              rows={3}
            />
          </div>
          <div className="flex items-center justify-between py-2 px-3 border border-border rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="public" className="text-sm font-medium">
                Make Public
              </Label>
              <p className="text-xs text-muted-foreground">
                Allow other organization members to view and duplicate this schema
              </p>
            </div>
            <Switch
              id="public"
              checked={isPublic}
              onCheckedChange={setIsPublic}
              disabled={isSaving}
            />
          </div>
          {nodes.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {nodes.length} {nodes.length === 1 ? 'table' : 'tables'} will be saved
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
            {isSaving ? (
              <>
                <RiLoader4Line className="h-4 w-4 mr-2 animate-spin" />
                {isUpdating ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              <>
                <RiCloudLine className="h-4 w-4 mr-2" />
                {isUpdating ? 'Update' : 'Save to Cloud'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
